import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { useCookies } from "react-cookie";
import {
  Search,
  Grid,
  BarChart2,
  Bell,
  Truck,
  Calendar,
  FileText,
  Monitor,
  ShoppingBag,
  Package,
  Award,
  Share2,
  MessageSquare,
  Globe,
  MapPin,
  Layers,
  Briefcase,
  Mail,
  Image as ImageIcon,
  Map,
  Gift,
  Navigation,
  ShoppingCart,
  Users,
  MessageCircle,
  CreditCard,
  DollarSign,
  Percent,
  Settings,
  Activity,
  Zap,
  UserCheck,
  Tag,
  PlusCircle,
  Send,
  Phone,
  Folder,
  BookOpen,
  RefreshCw,
  CornerDownLeft,
  ArrowUp,
  ArrowDown,
} from "react-feather";

/* -------------------------------------------------------------------------
 * Curated registry of navigable pages, with role-based visibility.
 * Add / tune entries here to expose more pages in the palette.
 * ------------------------------------------------------------------------- */
const ALL_ROLES = ["admin", "producer", "agent", "warehouse_manager"];

const PAGES = [
  // Overview
  { name: "Dashboard", path: "/dashboard", icon: Grid, keywords: "home overview main start", roles: ["admin", "producer", "agent"], group: "Overview" },
  { name: "Warehouse Dashboard", path: "/warehouse-manager", icon: Grid, keywords: "warehouse home overview", roles: ["warehouse_manager"], group: "Overview" },
  { name: "Sales", path: "/sales", icon: BarChart2, keywords: "sales orders revenue", roles: ["admin", "producer"], group: "Sales" },

  // Orders
  { name: "Order Reminders", path: "/order-reminders", icon: Bell, keywords: "reminders notifications followup", roles: ["admin", "producer"], group: "Orders" },
  { name: "View Orders", path: "/view-order", icon: ShoppingCart, keywords: "orders order purchases", roles: ["admin", "producer"], group: "Orders" },

  // Deliveries
  { name: "Deliveries", path: "/view-deliveries", icon: Truck, keywords: "delivery shipping ship", roles: ["admin", "producer"], group: "Deliveries" },
  { name: "Delivery Charges", path: "/delivery-charges", icon: DollarSign, keywords: "delivery charges pricing", roles: ["admin"], group: "Deliveries" },
  { name: "View Routes", path: "/view-delivery-day", icon: Navigation, keywords: "routes delivery day schedule", roles: ["admin", "producer"], group: "Deliveries" },
  { name: "Delivery Groups", path: "/delivery-groups", icon: Map, keywords: "delivery groups routes", roles: ["admin"], group: "Deliveries" },
  { name: "Add Delivery Day", path: "/add-delivery-day", icon: PlusCircle, keywords: "new delivery day route create add", roles: ["admin", "producer"], group: "Deliveries" },
  { name: "Holidays", path: "/view-holidays", icon: Calendar, keywords: "holidays days off vacation", roles: ["admin", "producer"], group: "Deliveries" },
  { name: "Add Holiday", path: "/add-holiday", icon: PlusCircle, keywords: "new holiday create add", roles: ["admin"], group: "Deliveries" },

  // Sessions / Operations
  { name: "Session Management", path: "/view-sessions", icon: Calendar, keywords: "sessions session", roles: ["admin", "producer"], group: "Operations" },
  { name: "Documents", path: "/documents", icon: FileText, keywords: "documents files docs", roles: ["admin"], group: "Operations" },
  { name: "POS", path: "/view-pos", icon: Monitor, keywords: "pos point of sale terminal cash register", roles: ["admin"], group: "Operations" },
  { name: "Store", path: "/view-store", icon: ShoppingBag, keywords: "store shop", roles: ["admin"], group: "Operations" },
  { name: "Warehouse Management", path: "/view-warehouse", icon: Package, keywords: "warehouse inventory stock", roles: ["admin"], group: "Operations" },
  { name: "Return Requests", path: "/return-requests", icon: RefreshCw, keywords: "return refund request", roles: ["admin"], group: "Operations" },

  // Programs
  { name: "Fidelity Program", path: "/view-fidelity-program", icon: Award, keywords: "fidelity loyalty rewards points", roles: ["admin"], group: "Programs" },
  { name: "Referral Program", path: "/view-referral-program", icon: Share2, keywords: "referral refer share invite", roles: ["admin"], group: "Programs" },
  { name: "Customer Interviews", path: "/customer-interviews", icon: MessageSquare, keywords: "interview customer feedback survey", roles: ["admin"], group: "Programs" },
  { name: "Create Interview", path: "/customer-interviews/create", icon: PlusCircle, keywords: "new interview create", roles: ["admin"], group: "Programs" },

  // Geography
  { name: "Regions", path: "/view-region", icon: Globe, keywords: "region area zone", roles: ["admin", "producer"], group: "Geography" },
  { name: "Add Region", path: "/add-new-region", icon: PlusCircle, keywords: "new region create add", roles: ["admin", "producer"], group: "Geography" },
  { name: "Locations", path: "/view-location", icon: MapPin, keywords: "location address city", roles: ["admin", "producer"], group: "Geography" },
  { name: "Add Location", path: "/add-new-location", icon: PlusCircle, keywords: "new location create add", roles: ["admin", "producer"], group: "Geography" },

  // Catalog
  { name: "Product Categories", path: "/product-categories", icon: Layers, keywords: "category categories taxonomy", roles: ["admin"], group: "Catalog" },
  { name: "View Producers", path: "/view-producers", icon: Briefcase, keywords: "producer suppliers vendors", roles: ["admin"], group: "Catalog" },
  { name: "Add Producer", path: "/add-producers", icon: PlusCircle, keywords: "new producer supplier vendor add create", roles: ["admin"], group: "Catalog" },
  { name: "View Products", path: "/view-product", icon: Package, keywords: "product catalog inventory list", roles: ["admin", "producer"], group: "Catalog" },
  { name: "Add Product", path: "/add-product", icon: PlusCircle, keywords: "new product create add", roles: ["admin", "producer"], group: "Catalog" },
  { name: "Promoted Products", path: "/view-promoted-product", icon: Tag, keywords: "promoted featured product", roles: ["admin"], group: "Catalog" },
  { name: "Product Import / Export", path: "/product-import-export", icon: RefreshCw, keywords: "import export csv excel product upload download", roles: ["admin", "warehouse_manager"], group: "Catalog" },
  { name: "Imported Products (Staging)", path: "/product-import-staging", icon: Folder, keywords: "staging import imported product", roles: ["admin", "warehouse_manager"], group: "Catalog" },
  { name: "Products in Region", path: "/view-productIn-region", icon: Map, keywords: "product region availability", roles: ["admin", "producer"], group: "Catalog" },
  { name: "Add Product in Region", path: "/add-productIn-region", icon: PlusCircle, keywords: "new product region availability", roles: ["admin", "producer"], group: "Catalog" },

  // Team
  { name: "View Agents", path: "/view-agents", icon: UserCheck, keywords: "agent sales rep salesperson", roles: ["producer"], group: "Team" },
  { name: "Add Agent", path: "/add-agents", icon: PlusCircle, keywords: "new agent create add", roles: ["producer"], group: "Team" },

  // Marketing
  { name: "View Offers", path: "/view-offer", icon: Gift, keywords: "offer discount deal promotion", roles: ["admin", "producer"], group: "Marketing" },
  { name: "Add Offer", path: "/add-offer", icon: PlusCircle, keywords: "new offer discount deal create", roles: ["admin", "producer"], group: "Marketing" },
  { name: "Coupon Codes", path: "/coupon-codes", icon: Tag, keywords: "coupon code voucher discount", roles: ["admin", "producer"], group: "Marketing" },
  { name: "Gifts", path: "/view-gifts", icon: Gift, keywords: "gift gifts free reward", roles: ["admin"], group: "Marketing" },

  // Reports
  { name: "Reports", path: "/view-report", icon: BarChart2, keywords: "report reports analytics", roles: ["admin", "producer"], group: "Reports" },
  { name: "Sales Dashboard", path: "/view-sales-report", icon: BarChart2, keywords: "sales dashboard report analytics revenue", roles: ["admin"], group: "Reports" },
  { name: "Picking List History", path: "/picking-list-history", icon: BookOpen, keywords: "picking list warehouse history", roles: ["admin", "producer", "warehouse_manager"], group: "Reports" },

  // Customers
  { name: "View Customers", path: "/view-customers", icon: Users, keywords: "customers users clients", roles: ["admin", "producer"], group: "Customers" },
  { name: "Customer Sales", path: "/customer-sales", icon: BarChart2, keywords: "customer sales spending", roles: ["admin", "producer"], group: "Customers" },
  { name: "Purchase Pattern", path: "/customer-purchase-pattern", icon: Activity, keywords: "purchase pattern analytics behavior", roles: ["admin", "producer"], group: "Customers" },
  { name: "Follow Ups", path: "/follow-ups", icon: Bell, keywords: "followup customer contact", roles: ["admin"], group: "Customers" },

  // Communication
  { name: "View Notifications", path: "/view-notifications", icon: Bell, keywords: "notifications push messages", roles: ["admin", "agent"], group: "Communication" },
  { name: "Send Notification", path: "/add-notifications", icon: Send, keywords: "send notification new push add", roles: ["admin", "agent"], group: "Communication" },
  { name: "Send WhatsApp", path: "/add-whatsapp", icon: Send, keywords: "whatsapp send message new", roles: ["admin", "agent"], group: "Communication" },
  { name: "WhatsApp Customers", path: "/whatsapp-customers", icon: MessageCircle, keywords: "whatsapp customers contact", roles: ["admin", "agent"], group: "Communication" },
  { name: "Sales WhatsApp", path: "/sales-whatsapp", icon: MessageCircle, keywords: "whatsapp sales agent", roles: ["admin", "agent"], group: "Communication" },
  { name: "Feedback", path: "/feedbacks", icon: MessageSquare, keywords: "feedback reviews ratings", roles: ["admin", "agent"], group: "Communication" },
  { name: "Contact", path: "/contact-customers", icon: Phone, keywords: "contact customers reach", roles: ["admin", "agent"], group: "Communication" },
  { name: "Issues", path: "/issues", icon: MessageCircle, keywords: "issues bugs problems support", roles: ["admin", "agent"], group: "Communication" },
  { name: "Emails History", path: "/view-emails-history", icon: Mail, keywords: "email history sent", roles: ["admin"], group: "Communication" },
  { name: "Emails CC / BCC", path: "/view-emails-cc-bcc", icon: Mail, keywords: "email cc bcc copy", roles: ["admin"], group: "Communication" },
  { name: "Content Templates", path: "/view-templates", icon: FileText, keywords: "template content email", roles: ["admin"], group: "Communication" },

  // Content
  { name: "Banners", path: "/view-banners", icon: ImageIcon, keywords: "banner image hero", roles: ["admin"], group: "Content" },
  { name: "Public Files", path: "/view-public-files", icon: Folder, keywords: "public files assets", roles: ["admin"], group: "Content" },

  // Settings
  { name: "View Payment Methods", path: "/view-payment", icon: CreditCard, keywords: "payment method gateway", roles: ["admin"], group: "Settings" },
  { name: "Add Payment Method", path: "/add-payment", icon: PlusCircle, keywords: "new payment method add gateway", roles: ["admin"], group: "Settings" },
  { name: "View Order Values", path: "/view-order-value", icon: DollarSign, keywords: "min order value minimum", roles: ["admin"], group: "Settings" },
  { name: "Add Order Value", path: "/add-order-value", icon: PlusCircle, keywords: "min order value new add", roles: ["admin"], group: "Settings" },
  { name: "View Tax Rates", path: "/view-tax-rate", icon: Percent, keywords: "tax rate vat gst", roles: ["admin"], group: "Settings" },
  { name: "Add Tax Rate", path: "/add-tax-rate", icon: PlusCircle, keywords: "new tax rate add vat", roles: ["admin"], group: "Settings" },
  { name: "App Settings", path: "/app-settings", icon: Settings, keywords: "settings configuration preferences", roles: ["admin"], group: "Settings" },
  { name: "Activity Logs", path: "/activity-logs", icon: Activity, keywords: "activity logs audit history", roles: ["admin"], group: "Settings" },
  { name: "Automations", path: "/automations", icon: Zap, keywords: "automation jobs scheduled tasks", roles: ["admin"], group: "Settings" },
];

const getCurrentRole = (cookies) => {
  try {
    const u = cookies && cookies.user;
    if (!u) return null;
    const parsed = typeof u === "string" ? JSON.parse(u) : u;
    return parsed && parsed.role ? parsed.role : null;
  } catch (e) {
    return null;
  }
};

const CommandPalette = ({ show, onClose }) => {
  const history = useHistory();
  const [cookies] = useCookies(["user"]);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const role = getCurrentRole(cookies);

  // Reset state every time the palette opens, and focus input
  useEffect(() => {
    if (show) {
      setQuery("");
      setActiveIdx(0);
      // Focus on next tick — modal needs to mount first
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 50);
    }
  }, [show]);

  const filtered = useMemo(() => {
    const visible = PAGES.filter((p) =>
      role ? p.roles.includes(role) : p.roles.some((r) => ALL_ROLES.includes(r))
    );
    const q = query.trim().toLowerCase();
    if (!q) return visible;
    const tokens = q.split(/\s+/).filter(Boolean);
    return visible.filter((item) => {
      const haystack = `${item.name} ${item.keywords} ${item.group} ${item.path}`.toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  }, [query, role]);

  // Keep activeIdx in range when filter changes
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const navigateTo = (path) => {
    onClose();
    setTimeout(() => history.push(path), 50);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = filtered[activeIdx];
      if (item) navigateTo(item.path);
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Scroll the active item into view when navigating with the keyboard
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${activeIdx}"]`);
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [activeIdx, filtered.length]);

  // Render flat list with group separators when group changes
  let lastGroup = null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered={false}
      dialogClassName="cmd-palette-dialog"
      backdropClassName="cmd-palette-backdrop"
      animation
    >
      <div className="cmd-palette" onKeyDown={handleKeyDown}>
        <div className="cmd-palette-search">
          <Search size={18} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search pages, settings, anything…"
            spellCheck="false"
            autoComplete="off"
          />
          <kbd>ESC</kbd>
        </div>

        <div className="cmd-palette-results" ref={listRef}>
          {filtered.length === 0 ? (
            <div className="cmd-palette-empty">
              <Search size={20} />
              <div>No matches for "{query}"</div>
              <small>Try fewer or different words</small>
            </div>
          ) : (
            filtered.map((item, idx) => {
              const showGroup = item.group !== lastGroup;
              lastGroup = item.group;
              const Icon = item.icon || Grid;
              const isActive = idx === activeIdx;
              return (
                <React.Fragment key={`${item.path}-${idx}`}>
                  {showGroup && (
                    <div className="cmd-palette-group">{item.group}</div>
                  )}
                  <div
                    data-idx={idx}
                    className={`cmd-palette-item ${isActive ? "active" : ""}`}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => navigateTo(item.path)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="cmd-palette-item-icon-wrap">
                      <Icon size={16} className="cmd-palette-item-icon" />
                    </span>
                    <span className="cmd-palette-item-name">{item.name}</span>
                    <span className="cmd-palette-item-path">{item.path}</span>
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        <div className="cmd-palette-footer">
          <span>
            <kbd>
              <ArrowUp size={11} />
            </kbd>
            <kbd>
              <ArrowDown size={11} />
            </kbd>
            <span>Navigate</span>
          </span>
          <span>
            <kbd>
              <CornerDownLeft size={11} />
            </kbd>
            <span>Open</span>
          </span>
          <span>
            <kbd>ESC</kbd>
            <span>Close</span>
          </span>
          <span className="cmd-palette-footer-spacer" />
          <span className="cmd-palette-footer-count">
            {filtered.length} {filtered.length === 1 ? "result" : "results"}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default CommandPalette;
